import { React, useState, useCallback } from "react";
import emailjs from 'emailjs-com';
import { Form, Input, TextArea, Button, Select } from 'semantic-ui-react';
import Swal from 'sweetalert2';
import styles from "../style";
import { contact, s10 } from "../assets/index"
import "../index.css"
import ReactWhatsapp from "react-whatsapp";
import Footer from "../components/Footer";
import { PiClockBold, PiMapPinFill, } from "react-icons/pi"
import { MdCall, MdEmail } from "react-icons/md";
import transition from "../components/transition";
import { FiChevronLeft, FiChevronRight, FiClock, FiMapPin } from "react-icons/fi";

const SERVICE_ID = "service_ciegiam";
const TEMPLATE_ID = "template_rbt9z1j";
const USER_ID = "Sl0PeCnudMpWpISoX";

const Contact = () => {
  // ----- STATE -----
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    user_phone: '',
    user_location: '',
    user_role: '',
    user_schoolname: '',
    user_priority: '',
    user_timeline: '',
    user_interest: '',
    user_message: '',
  });

  // Generate time slots from 8am to 5pm
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour <= 17; hour++) {
      for (let minute of [0]) {
        if (hour === 17 && minute === 30) continue;
        if (hour === 17 && minute === 0) continue;
        const ampm = hour < 12 ? 'AM' : 'PM';
        const displayHour = hour % 12 === 0 ? 12 : hour % 12;
        const minuteStr = minute.toString().padStart(2, '0');
        slots.push(`${displayHour}:${minuteStr} ${ampm}`);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // ----- Calendar helpers -----
  const getDaysInMonth = useCallback(() => {
    return new Date(currentYear, currentMonth + 1, 0).getDate();
  }, [currentYear, currentMonth]);

  const getFirstDayOfMonth = useCallback(() => {
    return new Date(currentYear, currentMonth, 1).getDay();
  }, [currentYear, currentMonth]);

  const getMonthName = useCallback(() => {
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    return monthNames[currentMonth];
  }, [currentMonth]);

  // ----- Navigation -----
  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // ----- Selection handlers -----
  const handleDaySelect = (day) => {
    setSelectedDay(new Date(currentYear, currentMonth, day));
  };

  const handleTimeSelect = (slot) => {
    setSelectedTimeSlot(slot);
  };

  // ----- Form handlers -----
  const handleInputChange = (e, { name, value }) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();

    if (!selectedDay || !selectedTimeSlot) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please select both a date and time for your session.',
      });
      return;
    }

    const formattedDate = `${selectedDay.getFullYear()}-${String(
      selectedDay.getMonth() + 1
    ).padStart(2, '0')}-${String(selectedDay.getDate()).padStart(2, '0')}`;
    const selectedDateTime = `${formattedDate} ${selectedTimeSlot}`;

    const templateParams = {
      ...formData,
      selected_datetime: selectedDateTime,
    };

    console.log('Sending:', templateParams); // remove after testing

    emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, USER_ID).then(
      (result) => {
        console.log(result.text);
        Swal.fire({
          icon: 'success',
          title: 'Message Sent Successfully',
          text: `Your session has been booked for ${selectedDateTime}`,
        });

        setSelectedDay(null);
        setSelectedTimeSlot(null);
        setFormData({
          user_name: '',
          user_email: '',
          user_phone: '',
          user_location: '',
          user_role: '',
          user_schoolname: '',
          user_priority: '',
          user_timeline: '',
          user_interest: '',
          user_message: '',
        });
      },
      (error) => {
        console.log(error.text);
        Swal.fire({
          icon: 'error',
          title: 'Ooops, something went wrong',
          text: error.text,
        });
      }
    );
  };

  // ----- Summary text -----
  const getSummaryText = () => {
    if (selectedDay && selectedTimeSlot) {
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `🗓️ ${selectedDay.getDate()} ${monthNames[selectedDay.getMonth()]} ${selectedDay.getFullYear()} · ${selectedTimeSlot}`;
    } else if (selectedDay) {
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `🗓️ ${selectedDay.getDate()} ${monthNames[selectedDay.getMonth()]} ${selectedDay.getFullYear()} · ⏳ pick time`;
    }
    return '📌 Select a date & time';
  };

  // ----- Render calendar days -----
  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth();
    const firstDayOfMonth = getFirstDayOfMonth();
    const days = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(
        <div key={`empty-${i}`} className="aspect-square flex items-center justify-center"></div>
      );
    }

    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(currentYear, currentMonth, day);
      const isToday = dayDate.getTime() === todayDate.getTime();
      const isSelected = selectedDay &&
        selectedDay.getFullYear() === currentYear &&
        selectedDay.getMonth() === currentMonth &&
        selectedDay.getDate() === day;

      days.push(
        <div
          key={day}
          onClick={() => handleDaySelect(day)}
          className={`
            m-2 flex items-center justify-center rounded-md cursor-pointer 
            transition text-sm font-medium hover:bg-fourth
            ${isToday ? 'border-2 border-blue-300 font-bold' : ''}
            ${isSelected ? 'bg-fourth text-white font-semibold shadow-lg' : ''}
          `}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  // ----- Render time slots -----
  const renderTimeSlots = () => {
    return timeSlots.map(slot => {
      const isSelected = selectedTimeSlot === slot;

      return (
        <div
          key={slot}
          onClick={() => handleTimeSelect(slot)}
          className={`
            px-3 py-2 rounded-md bg-gray-50 border border-gray-300 
            text-xs font-medium text-fourth cursor-pointer transition 
            hover:bg-fourth
            ${isSelected ? 'bg-fourth text-white font-semibold shadow-lg' : ''}
          `}
        >
          {slot}
        </div>
      );
    });
  };

  return (
    <div className={`flex flex-col`}>
      <div className={`${styles.flexCenter} gap-20 flex flex-col`}>
        <div className="relative w-full">
          <div className={`absolute z-10 w-full flex flex-col gap-4 py-40 px-20`}>
            <div className={`${styles.flexStart} flex flex-col `}>
              <h2 className={`xs:text-lg text-md font-bold text-black`}>
                Start a conversation
              </h2>
              <p className={`xs:text-sm text-ss mt-4 max-w-[312px] text-black`}>
                To enquire about working with Aegis Education Group, please share a little about your school, your current priorities and the support you are looking for.
              </p>
            </div>
            <div className="py-10 flex md:flex-row flex-col justify-center items-start md:gap-40 gap-20">
              <div className={`${styles.flexStart} flex flex-col gap-4`}>
                <div className="flex flex-row gap-4 items-start">
                  <PiMapPinFill size={20} color={'black'} />
                  <div className="flex flex-col gap-2">
                    <p className="font-lato font-bold xs:text-mm text-sm leading-[27px] text-fourth">Office Address</p>
                    <p className="font-lato font-regular xs:text-sm text-ss leading-[27px] text-black">Ethiopia</p>
                  </div>
                </div>
                <div className="flex flex-row gap-4 items-start">
                  <MdEmail size={20} color={'black'} />
                  <div className="flex flex-col gap-2">
                    <p className="font-lato font-bold xs:text-mm text-sm leading-[27px] text-fourth">Mail</p>
                    <p className="font-lato font-regular xs:text-sm text-ss leading-[27px] text-black">emilianne@aegiseducationgroup.com </p>
                  </div>
                </div>
              </div>

              <div className={`${styles.flexStart} flex flex-col gap-4`}>
                <div className="flex flex-row gap-4 items-start">
                  <MdCall size={20} color={'black'} />
                  <div className="flex flex-col gap-2">
                    <p className="font-lato font-bold xs:text-mm text-sm leading-[27px] text-fourth">Phone</p>
                    <p className="font-lato font-regular xs:text-sm text-ss leading-[27px] text-black">+254 723 493 280</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <img src={contact} alt="" className="relative w-full h-[840px] md:h-[600px] object-cover" />
        </div>

        <div className={`${styles.flexCenter} flex flex-col w-full max-w-[600px] mb-20 px-4`}
          style={{
            backgroundImage: `url(${s10})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            zIndex: 0,
          }}
        >
          <Form onSubmit={handleOnSubmit} className="w-full">
            <div className="flex flex-col gap-2 items-center">
              <p className="text-mm font-semibold my-10 max-w-[340px] text-black text-center">
                Select Date & Time
              </p>

              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <div className="flex flex-row items-center gap-2 bg-gray-100 h-[40px] px-4 rounded-lg justify-center">
                  <FiClock size={16} className="text-gray-600" />
                  <h2 className="text-xs font-medium whitespace-nowrap">
                    (M-F) 8.00AM - 5.00PM
                  </h2>
                </div>
                <div className="flex flex-row items-center gap-2 bg-gray-100 h-[40px] px-4 rounded-lg justify-center">
                  <FiMapPin size={16} className="text-gray-800" />
                  <h2 className="text-xs font-medium whitespace-nowrap">
                    Ethiopia
                  </h2>
                </div>
              </div>

              {/* Calendar Date Selection */}
              <div className="w-full my-6">
                <div className="bg-white rounded-xl p-2 sm:p-4">
                  <div className="flex items-center justify-between mb-4 px-2">
                    <button
                      type="button"
                      onClick={handlePrevMonth}
                      className="flex items-center justify-center rounded-full hover:bg-gray-200 transition text-gray-700 text-mm font-semibold focus:outline-none p-2"
                      aria-label="Previous month"
                    >
                      <FiChevronLeft size={20} />
                    </button>
                    <span className="text-sm sm:text-mm font-semibold text-gray-800">
                      {getMonthName()} {currentYear}
                    </span>
                    <button
                      type="button"
                      onClick={handleNextMonth}
                      className="flex items-center justify-center rounded-full hover:bg-gray-200 transition text-gray-700 text-mm font-semibold focus:outline-none p-2"
                      aria-label="Next month"
                    >
                      <FiChevronRight size={20} />
                    </button>
                  </div>

                  <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="text-center text-[10px] sm:text-xs font-semibold text-gray-500 uppercase py-1">
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
                    {renderCalendarDays()}
                  </div>
                </div>
              </div>

              {/* Time Selection */}
              <div className="w-full my-10 items-center flex flex-col">
                <label className="text-center text-mm font-semibold text-gray-700 my-6">Available Time</label>
                <div className="w-full px-2 sm:px-4">
                  <div className="flex flex-wrap gap-2 sm:gap-4 items-center justify-center">
                    {renderTimeSlots()}
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full my-10 items-center flex flex-col">
              <label className="text-center text-mm font-semibold text-gray-700">Details</label>
            </div>

            <div className="flex flex-col w-full max-w-[600px] mx-auto gap-4">
              <div className="flex flex-col sm:flex-row gap-4 w-full">
                <Form.Field
                  id='form'
                  className="w-full sm:flex-1 sm:min-w-0"
                  control={Input}
                  name='user_name'
                  value={formData.user_name}
                  onChange={handleInputChange}
                  placeholder='Name…'
                  required
                  icon='user circle'
                  iconPosition='left'
                />
                <Form.Field
                  id='form-input-control-email'
                  className="w-full sm:flex-1 sm:min-w-0"
                  control={Input}
                  name='user_email'
                  value={formData.user_email}
                  onChange={handleInputChange}
                  placeholder='Email…'
                  required
                  icon='mail'
                  iconPosition='left'
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 w-full">
                <Form.Field
                  id='form-input-control-phone'
                  className="w-full sm:flex-1 sm:min-w-0"
                  control={Input}
                  name='user_phone'
                  value={formData.user_phone}
                  onChange={handleInputChange}
                  placeholder='Phone Number'
                  required
                  icon='phone'
                  iconPosition='left'
                />
                <Form.Field
                  id='form-input-control-location'
                  className="w-full sm:flex-1 sm:min-w-0"
                  control={Input}
                  name='user_location'
                  value={formData.user_location}
                  onChange={handleInputChange}
                  placeholder='Country / City'
                  required
                  icon='pin'
                  iconPosition='left'
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 w-full">
                <Form.Field
                  id='form-input-control-role'
                  className="w-full sm:flex-1 sm:min-w-0"
                  control={Input}
                  name='user_role'
                  value={formData.user_role}
                  onChange={handleInputChange}
                  placeholder='Role'
                  required
                  icon='shield'
                  iconPosition='left'
                />
                <Form.Field
                  id='form-input-control-schoolname'
                  className="w-full sm:flex-1 sm:min-w-0"
                  control={Input}
                  name='user_schoolname'
                  value={formData.user_schoolname}
                  onChange={handleInputChange}
                  placeholder='School Name'
                  required
                  icon='home'
                  iconPosition='left'
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 w-full">
                <Form.Field
                  id='form-input-control-priority'
                  className="w-full sm:flex-1 sm:min-w-0"
                  control={Input}
                  name='user_priority'
                  value={formData.user_priority}
                  onChange={handleInputChange}
                  placeholder='Main Priority'
                  required
                  icon='warning'
                  iconPosition='left'
                />
                <Form.Field
                  id='form-input-control-timeline'
                  className="w-full sm:flex-1 sm:min-w-0"
                  control={Input}
                  name='user_timeline'
                  value={formData.user_timeline}
                  onChange={handleInputChange}
                  placeholder='Preferred Timeline'
                  required
                  icon='clock'
                  iconPosition='left'
                />
              </div>

              <Form.Field
                id='form-input-control-interest'
                className="w-full"
                control={Select}
                name='user_interest'
                value={formData.user_interest}
                onChange={handleInputChange}
                placeholder='Area of Interest'
                required
                icon='dropdown'
                iconPosition='left'
                options={[
                  {
                    key: 'safeguarding',
                    text: 'Safeguarding & Child Protection',
                    value: 'Safeguarding & Child Protection',
                  },
                  {
                    key: 'school_culture',
                    text: 'School Culture & Staff Practice',
                    value: 'School Culture & Staff Practice',
                  },
                  {
                    key: 'safer_recruitment',
                    text: 'Safer Recruitment Advisory',
                    value: 'Safer Recruitment Advisory',
                  },
                  {
                    key: 'implementation',
                    text: 'Ongoing Implementation Support',
                    value: 'Ongoing Implementation Support',
                  },
                  {
                    key: 'not_sure',
                    text: 'Not sure yet',
                    value: 'Not sure yet',
                  }
                ]}
                style={{
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}
              />

              <Form.Field
                id='form-textarea-control-message'
                className="w-full"
                control={TextArea}
                name='user_message'
                value={formData.user_message}
                onChange={handleInputChange}
                placeholder='Your Message…'
                required
              />

              <Button type='submit' color="green" className={`${styles.flexCenter} my-2 w-full`}>
                Send Message
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;